
import { AssessmentResponse, AssessmentResult, UserProfile, RecommendedCareer } from '@/types/assessment';
import { toast } from 'sonner';
import { generateOpenAIReport } from '../openai/openaiService';
import { generateBasicReport, generateFallbackReport, generateErrorReport } from './basicReportGenerator';
import { saveReportToLocalStorage, saveReportToDatabase } from './reportStorageService';
import { getOpenAIKey } from '../apiKeyUtils';
import { generateCareerRecommendations } from './careerRecommendationService';
import { extractStudentInfo } from './studentInfoService';
import { validateSession, validateUserId } from '../auth/sessionService';
import { saveResponsesToLocalStorage } from './reportStorageService';
import { supabase } from '@/integrations/supabase/client';

export const generateReport = async (userId: string, responses: AssessmentResponse[]): Promise<AssessmentResult> => {
  try {
    console.log('Starting report generation for user:', userId);
    
    // Check authentication first
    const { validSession, userId: sessionUserId, sessionError } = await validateSession();
    
    // Validate and normalize user ID
    const { userId: validatedUserId, isAuthenticated, isValidId } = validateUserId(userId, sessionUserId);
    userId = validatedUserId; // Use the validated ID from now on
    
    // Extract order ID from the first response (all responses should have the same order ID)
    const orderId = responses[0]?.orderId;
    console.log('Generating report for order ID:', orderId || 'No order ID');
    
    // Save responses to localStorage as a backup
    saveResponsesToLocalStorage(userId, responses);
    
    // Generate career recommendations
    const recommendedCareers = generateCareerRecommendations(responses);

    // Get student information
    const studentInfo = await extractStudentInfo(userId, responses);
    
    // Check for OpenAI API key
    let openAIKey = '';
    try {
      openAIKey = await getOpenAIKey();
      console.log('OpenAI key retrieved:', openAIKey ? 'Key found' : 'No key found');
    } catch (e) {
      console.error('Error fetching OpenAI key:', e);
    }

    // If no OpenAI API key, generate a basic report
    if (!openAIKey) {
      console.warn('No OpenAI API key found. Generating a basic report.');
      toast.warning('OpenAI API key not found. Generating a basic report.');
      
      // Generate a simple report without OpenAI
      const basicReport = generateBasicReport(responses);
      
      const result: AssessmentResult = {
        id: userId,
        userId,
        report: basicReport,
        pdfUrl: '',
        createdAt: new Date(),
        orderId: orderId,
        recommendedCareers
      };
      
      // Try to save the basic report to Supabase if we have a valid session and UUID
      if (isAuthenticated && isValidId) {
        const savedReport = await saveReportToDatabase(userId, basicReport, '', orderId);
        if (savedReport) {
          result.id = savedReport.id;
        }
      } else {
        console.log('Not saving to Supabase - invalid session or user ID');
      }
      
      // Always cache the report in localStorage
      saveReportToLocalStorage(userId, result);
      
      return result;
    }

    console.log('OpenAI API key found, generating AI report with student info');

    // Generate report using OpenAI with student info
    try {
      const report = await generateOpenAIReport(responses, studentInfo);
      
      // Create the result object
      const aiResult: AssessmentResult = {
        id: userId,
        userId,
        report,
        pdfUrl: '',
        createdAt: new Date(),
        orderId: orderId,
        recommendedCareers
      };
      
      // Always cache the report in localStorage
      saveReportToLocalStorage(userId, aiResult);
      
      // Save the AI-generated report to Supabase if we have a valid session and UUID
      if (isAuthenticated && isValidId) {
        const savedReport = await saveReportToDatabase(userId, report, '', orderId);
        if (savedReport) {
          aiResult.id = savedReport.id;
        }
      } else {
        console.log('Not saving report to Supabase - invalid session or user ID');
        toast.warning('Report is only available temporarily as it could not be saved to your account.');
      }

      return aiResult;
      
    } catch (openAIError) {
      console.error('Error calling OpenAI API:', openAIError);
      toast.error('Error generating AI report. Falling back to basic report.');
      
      // Create a fallback report
      return await createFallbackReport(userId, responses, orderId, recommendedCareers, isAuthenticated, isValidId);
    }
  } catch (error) {
    console.error('Error in generateReport:', error);
    toast.error('Error generating report');
    
    // Return a basic error report and ensure it's saved to localStorage
    const errorReport: AssessmentResult = {
      id: userId,
      userId,
      report: generateErrorReport(),
      pdfUrl: '',
      createdAt: new Date(),
    };
    
    saveReportToLocalStorage(userId, errorReport);
    
    return errorReport;
  }
};

// Helper function to create a fallback report when OpenAI fails
const createFallbackReport = async (
  userId: string, 
  responses: AssessmentResponse[], 
  orderId: string | undefined,
  recommendedCareers: RecommendedCareer[],
  isAuthenticated: boolean,
  isValidId: boolean
): Promise<AssessmentResult> => {
  // Import questions here to avoid circular dependencies
  const { questionData: questions } = await import('@/data/questions');
  
  // Format the assessment data for fallback report
  const formattedResponses = responses.map(response => {
    const question = questions.find(q => q.id === response.questionId);
    return {
      question: question?.text || `Question ${response.questionId}`,
      category: question?.category || 'Unknown',
      answer: response.answer
    };
  });

  // Group by category for better context
  const categorizedResponses = formattedResponses.reduce((acc, response) => {
    if (!acc[response.category]) {
      acc[response.category] = [];
    }
    acc[response.category].push({ question: response.question, answer: response.answer });
    return acc;
  }, {} as Record<string, { question: string, answer: string }[]>);
  
  // Generate a fallback report
  const fallbackReport = generateFallbackReport(categorizedResponses);
  
  const fallbackResult: AssessmentResult = {
    id: userId,
    userId,
    report: fallbackReport,
    pdfUrl: '',
    createdAt: new Date(),
    orderId: orderId,
    recommendedCareers
  };
  
  // Save to localStorage
  saveReportToLocalStorage(userId, fallbackResult);
  
  // Try to save the fallback report to Supabase if we have a valid session and UUID
  if (isAuthenticated && isValidId) {
    const savedReport = await saveReportToDatabase(userId, fallbackReport, '', orderId);
    if (savedReport) {
      fallbackResult.id = savedReport.id;
    }
  }
  
  return fallbackResult;
};
